<template>
  <div>
    <Panel :module="panel.module" :title="panel.title" :showSearch="panel.showSearch" :showButtons="panel.showButtons"
      :searchExtraActive="hasActiveFilter" :onSearchRemoveExtraFilter="resetAllFilters">
      <div slot="content-buttons">
        <Button _key="btnPayableReceivableCreate" type="primary" title="Pagar e Receber" classIcon="fa-solid fa-plus"
          size="small" :clicked="create" />
        <SideBar title="Pagar e Receber">
          <div v-if="sideBar.open">
            <PayableReceivableCreateUpdate :key="`${id}-${cloneMode ? 'clone' : 'edit'}`" :id="id"
              :cloneMode="cloneMode" :reloadList="list" />
          </div>
        </SideBar>
      </div>
      <div slot="content-main">
        <PayableReceivableFilterBar :filterState="filterState" @change="onFilterChange" />

        <div class="payable-receivable-list-main-filter">
          <div class="payable-receivable-list-main-filters-group">
            <FilterQuery :filters="filters" :value="filterQueryModelForFilterQuery" @input="onFilterQueryModelChange" />
          </div>
          <TableTotalPerPage :totalRecords="totalRecords" />
        </div>

        <PayableReceivableTable class="mt-4" :items="items" :showClosed="showClosed"
          :hasPaidNameFilterActive="hasPaidNameFilterActive" :totalRecords="totalRecords" :loading="loading"
          :totalizations="totalizations" @page-change="setPageAndReload" @reload="list"
          @clear-search="clearSearchAndReload" @set-search="setSearchAndReload"
          @set-total-per-page="setTotalPerPageAndReload" />

        <PayableReceivableSummary v-if="showSummary && hasRecords" :prevBalance="summaryData.prevBalance"
          :totalPagar="summaryData.totalPagar" :totalReceber="summaryData.totalReceber"
          :balanceType="summaryData.balanceType" />
      </div>
    </Panel>
  </div>
</template>

<script>
import Panel from "@/ui/layout/Panel";
import SideBar from "@/ui/forms/SideBar.vue";
import Button from "@/ui/forms/Button.vue";
import FilterQuery from "@/ui/shared/filter-builder/FilterQuery.vue";
import TableTotalPerPage from "@/ui/shared/TableTotalPerPage.vue";
import PayableReceivableFilterBar from "./PayableReceivableFilterBar.vue";
import PayableReceivableTable from "./PayableReceivableTable.vue";
import PayableReceivableSummary from "./PayableReceivableSummary.vue";
import PayableReceivableCreateUpdate from "./PayableReceivableCreateUpdate.vue";
import {
  createPayableReceivableDefaultFilterState,
  defaultPanel,
  getPayableReceivableDueDateOrderValue,
  getPayableReceivableConfigSelection,
  hasPayableReceivableFilterValue,
  isPayableReceivableDueDateOrderByFilter,
  isPayableReceivableDueDateValueFilter,
  isPayableReceivablePaidNameFilter,
  isPayableReceivableSearchFilter,
  isReservedPayableReceivableFilter,
  normalizePayableReceivableTitle,
  payableReceivableDueDateOrderField,
  payableReceivableFilters,
  payableReceivableOrderByTitle,
} from "../../services/payableReceivableList.config.js";
import PayableReceivableService from "../../services/payableReceivable.service.js";

import { mapGetters, mapMutations, mapState } from "vuex";

function buildFilter(state) {
  const currentFilterQueryModel = Array.isArray(state.filterState?.filterQueryModel)
    ? state.filterState.filterQueryModel
    : [];
  const filterQueryModel = currentFilterQueryModel.filter((item) => item && item.value);
  const orderByFilter = {
    title: payableReceivableOrderByTitle,
    field: payableReceivableDueDateOrderField,
    value: getPayableReceivableDueDateOrderValue(state.configSelection),
  };
  const stringFilter = [
    ...filterQueryModel.filter((item) => !isPayableReceivableDueDateOrderByFilter(item)),
    orderByFilter,
  ];

  return {
    StringFilter: stringFilter.length ? JSON.stringify(stringFilter) : undefined,
    CurrentPage: state.currentPage,
    TotalPerPage: state.totalPerPage,
  };
}

const initialConfigSelection = getPayableReceivableConfigSelection();

export default {
  name: "PayableReceivableListMain",
  components: {
    Panel,
    SideBar,
    Button,
    FilterQuery,
    TableTotalPerPage,
    PayableReceivableFilterBar,
    PayableReceivableTable,
    PayableReceivableSummary,
    PayableReceivableCreateUpdate,
  },
  data() {
    return {
      configSelection: [...initialConfigSelection],
      id: "",
      cloneMode: false,
      showSummary: initialConfigSelection.includes("showSummary"),
      showClosed: initialConfigSelection.includes("showClosed"),
      items: [],
      totalizations: [],
      totalRecords: 0,
      loading: false,
      filterState: createPayableReceivableDefaultFilterState(),
      currentPage: 1,
      totalPerPage: 10,
      panel: defaultPanel,
      filters: payableReceivableFilters,
    };
  },
  mounted() {
    this.list();
  },
  computed: {
    ...mapGetters("generic", ["event"]),
    ...mapState("generic", ["sideBar"]),
    hasRecords() {
      return Array.isArray(this.items) && this.items.length > 0;
    },
    hasPaidNameFilterActive() {
      const currentFilters = Array.isArray(this.filterState?.filterQueryModel)
        ? this.filterState.filterQueryModel
        : [];

      return currentFilters.some((item) => isPayableReceivablePaidNameFilter(item) && hasPayableReceivableFilterValue(item?.value));
    },
    filterQueryModelForFilterQuery() {
      return Array.isArray(this.filterState.filterQueryModel)
        ? this.filterState.filterQueryModel.filter((item) => !isReservedPayableReceivableFilter(item))
        : [];
    },
    hasActiveFilter() {
      const filterQueryModel = Array.isArray(this.filterState.filterQueryModel)
        ? this.filterState.filterQueryModel
        : [];

      const searchItem = filterQueryModel.find(isPayableReceivableSearchFilter);
      const hasSearch = hasPayableReceivableFilterValue(searchItem?.value);

      const hasAdvancedFilter = filterQueryModel
        .filter((item) => !isReservedPayableReceivableFilter(item))
        .some((item) => hasPayableReceivableFilterValue(item?.value));

      const hasScope = this.filterState.scope === "all"
        || this.filterState.scope === "today"
        || this.filterState.mode === "period";

      return hasSearch || hasAdvancedFilter || hasScope;
    },
    summaryData() {
      const normalizedTotals = (this.totalizations || []).map((item) => ({
        key: normalizePayableReceivableTitle(item?.title),
        value: Number(item?.value) || 0,
      }));

      const findValue = (key) => {
        const found = normalizedTotals.find((item) => item.key === key);
        return found ? found.value : 0;
      };

      const prevBalance = findValue("saldo");
      const totalPagar = findValue("a-pagar");
      const totalReceber = findValue("a-receber");

      return {
        prevBalance,
        totalPagar,
        totalReceber,
        balanceType: prevBalance >= 0 ? "positive" : "negative",
      };
    },
  },
  methods: {
    ...mapMutations("generic", ["openSideBar", "removeLoading"]),
    mergeFilterQueryModel(filterQueryModel) {
      const filters = Array.isArray(filterQueryModel) ? filterQueryModel : [];
      const current = Array.isArray(this.filterState.filterQueryModel) ? this.filterState.filterQueryModel : [];
      const reserved = current.filter((item) => isReservedPayableReceivableFilter(item));

      return [
        ...filters.filter((item) => !isReservedPayableReceivableFilter(item)),
        ...reserved,
      ];
    },
    async list() {
      this.loading = true;
      try {
        const content = await PayableReceivableService.list(buildFilter(this));
        this.items = content?.items || [];
        this.totalizations = content?.totalizations || [];
        this.totalRecords = content?.totalRecords || 0;
      } finally {
        this.loading = false;
        this.removeLoading(["panel", "loadingLine", "btnSearch", "btnClean"]);
      }
    },
    async applyFilterState(filterState) {
      const incomingFqm = Array.isArray(filterState.filterQueryModel) ? filterState.filterQueryModel : [];
      const current = Array.isArray(this.filterState.filterQueryModel) ? this.filterState.filterQueryModel : [];
      const nonDueDate = current.filter((item) => !isPayableReceivableDueDateValueFilter(item));
      const newDueDate = incomingFqm.filter((item) => isPayableReceivableDueDateValueFilter(item));

      this.filterState = {
        ...this.filterState,
        ...filterState,
        filterQueryModel: [...nonDueDate, ...newDueDate],
      };
      this.currentPage = 1;
      return this.list();
    },
    async setPageAndReload(page) {
      this.currentPage = page;
      return this.list();
    },
    async setTotalPerPageAndReload(totalPerPage) {
      this.totalPerPage = totalPerPage;
      this.currentPage = 1;
      return this.list();
    },
    async setSearchAndReload({ search, filter }) {
      const currentFilters = Array.isArray(this.filterState.filterQueryModel)
        ? this.filterState.filterQueryModel.filter((item) => !isPayableReceivableSearchFilter(item))
        : [];

      if (search) {
        currentFilters.push({
          field: "Search",
          type: "text",
          value: search,
          filterType: filter || "contains",
        });
      }

      this.filterState = { ...this.filterState, filterQueryModel: currentFilters };
      this.currentPage = 1;
      return this.list();
    },
    async clearSearchAndReload() {
      return this.setSearchAndReload({ search: "", filter: "contains" });
    },
    onFilterChange(filterState) {
      return this.applyFilterState(filterState);
    },
    onFilterQueryModelChange(filterQueryModel) {
      this.filterState = {
        ...this.filterState,
        filterQueryModel: this.mergeFilterQueryModel(filterQueryModel),
      };
    },
    create() {
      this.id = "";
      this.cloneMode = false;
      this.openSideBar();
      this.removeLoading(["btnPayableReceivableCreate"]);
    },
    resetAllFilters() {
      this.filterState = createPayableReceivableDefaultFilterState();
      this.currentPage = 1;
      this.list();
    },
  },
  watch: {
    event: {
      handler(event) {
        if (event.name === "payableReceivableUpdate") {
          this.cloneMode = false;
          this.id = event.data.id;
          this.openSideBar();
        }
        if (event.name === "payableReceivableClone") {
          this.cloneMode = true;
          this.id = event.data.id;
          this.openSideBar();
        }
        if (event.name === "payableReceivableToggleSummary") {
          this.showSummary = !!event.data?.showSummary;
        }
        if (event.name === "payableReceivableToggleClosed") {
          this.showClosed = !!event.data?.showClosed;
        }
        if (event.name === "payableReceivableToggleDueDateOrder") {
          const nextSelection = Array.isArray(event.data?.configSelection)
            ? event.data.configSelection
            : [...this.configSelection];
          const currentOrderValue = getPayableReceivableDueDateOrderValue(this.configSelection);
          const nextOrderValue = event.data?.orderValue || getPayableReceivableDueDateOrderValue(nextSelection);

          this.configSelection = [...nextSelection];

          if (currentOrderValue !== nextOrderValue) {
            this.currentPage = 1;
            this.list();
          }
        }
        if (event.name === "stringFilter") {
          const filterBuilderFilters = Array.isArray(event.data) ? event.data : [];
          const current = Array.isArray(this.filterState.filterQueryModel)
            ? this.filterState.filterQueryModel
            : [];
          const reserved = current.filter((item) => isReservedPayableReceivableFilter(item));
          this.filterState = {
            ...this.filterState,
            filterQueryModel: [
              ...reserved,
              ...filterBuilderFilters.filter((item) => !isReservedPayableReceivableFilter(item)),
            ],
          };
          this.currentPage = 1;
          this.list();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.payable-receivable-list-main-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 18px 0px 0px 0px;
}

.payable-receivable-list-main-filter> :last-child {
  flex-shrink: 0;
}

@media (min-width: 769px) {
  .payable-receivable-list-main-filter {
    flex-wrap: nowrap;
  }
}

.payable-receivable-list-main-filters-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.payable-receivable-list-main-search-tag {
  margin-left: 2px;
}
</style>
